import { useState } from "react";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ArchiveRestore,
  CheckCircle,
  AlertTriangle,
  Info,
  Folder,
  RotateCcw,
} from "lucide-react";
import { ProjectAPI, Project } from "@/api/projects";

interface RestoreProjectProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onSuccess?: () => void;
}

export function RestoreProject({ isOpen, onClose, project, onSuccess }: RestoreProjectProps) {
  const [isRestoring, setIsRestoring] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    if (!project) return;

    setIsRestoring(true);
    setError(null);

    try {
      const response = await ProjectAPI.restore(project.id);

      if (response.code === '200') {
        setIsRestoring(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
          setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to restore project');
      }
    } catch (error) {
      setIsRestoring(false);
      setError(error instanceof Error ? error.message : 'Failed to restore project. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <ArchiveRestore className="w-5 h-5 text-blue-600" />
            <span>Restore Project</span>
          </DialogTitle>
          <DialogDescription>
            This action will restore the project and make it active again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {isComplete ? (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Restored Successfully!</h3>
              <p className="text-muted-foreground mb-4">
                "{project?.name}" has been restored and is now active.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    The project is now available for scanning and can be archived again if needed.
                  </p>
                </div>
              </div>
            </div>
          ) : isRestoring ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Restoring Project...</h3>
              <p className="text-muted-foreground">Moving "{project?.name}" back to active projects...</p>
            </div>
          ) : (
           <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to restore the project <strong>{project?.name}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
          )}

          {!isRestoring && !isComplete && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestore}
                className="bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Restore Project
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}